import React from 'react';
import { Settings } from 'lucide-react';
import { ScrollableModal } from '../../components/ui/ScrollableModal';
import DropdownSearch from '../../components/ui/DropdownSearch';
import MultiSelectDropdownSearch from '../../components/ui/MultiSelectDropdownSearch';

export default function RepositorySettingsModal({
  isOpen,
  onClose,
  savingBranch,
  branches,
  branchesLoading,
  selectedDeploymentBranches,
  onSelectedDeploymentBranchesChange,
  customDeploymentBranchesText,
  onCustomDeploymentBranchesTextChange,
  settingsQaBranch,
  onSettingsQaBranchChange,
  settingsUatBranch,
  onSettingsUatBranchChange,
  cycleBotLoginsText,
  onCycleBotLoginsTextChange,
  onCycleBotLoginsInteraction,
  onResetCycleBotDefaults,
  onSave,
}: any) {
  return (
    <ScrollableModal
      isOpen={isOpen}
      onClose={onClose}
      title="Repository settings"
      icon={<Settings className="w-5 h-5 text-vivid-red" />}
      disableClose={savingBranch}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={savingBranch}
            className="btn-secondary-mdl"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={savingBranch}
            className="btn-primary-mdl border-2 border-transparent disabled:opacity-50"
          >
            {savingBranch ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label
            htmlFor="deployment-branches"
            className="block text-sm font-medium text-slate-300 mb-1"
          >
            Deployment branches (required)
          </label>
          <p className="text-xs text-slate-400 mb-2">
            DORA metrics and commit sync use all selected branches. The first branch is primary for
            DORA labels and tech stack scans.
          </p>
          {branchesLoading ? (
            <p className="text-sm text-slate-400">Loading branches…</p>
          ) : branches.length > 0 ? (
            <MultiSelectDropdownSearch
              id="deployment-branches"
              options={branches}
              selectedIds={selectedDeploymentBranches}
              onChange={onSelectedDeploymentBranchesChange}
              getOptionValue={(b) => b.name}
              getOptionLabel={(b) => b.name}
              getOptionSublabel={(b) => (b.isDefault ? '(GitHub default)' : '')}
              placeholder="Search branches to add…"
              emptyMessage="No branches match your search"
              disabled={savingBranch}
              comboboxAriaLabel="Deployment branches"
            />
          ) : (
            <input
              id="deployment-branches"
              type="text"
              value={customDeploymentBranchesText}
              onChange={(e) => onCustomDeploymentBranchesTextChange(e.target.value)}
              placeholder="e.g. main, release"
              className="input-mdl"
            />
          )}
          {!branchesLoading && branches.length === 0 && (
            <p className="mt-1 text-xs text-slate-400">
              Sync the repository to load branches, or enter comma-separated branch names above.
            </p>
          )}
        </div>
        <div>
          <label htmlFor="qa-branch" className="block text-sm font-medium text-slate-300 mb-1">
            QA branch (optional)
          </label>
          {branches.length > 0 ? (
            <DropdownSearch
              id="qa-branch"
              options={[{ name: '' }, ...branches]}
              value={
                settingsQaBranch
                  ? (branches.find((b) => b.name === settingsQaBranch) ?? {
                      name: settingsQaBranch,
                    })
                  : { name: '' }
              }
              onChange={(b) => onSettingsQaBranchChange(b?.name ?? '')}
              getOptionValue={(b) => b.name}
              getOptionLabel={(b) => (b.name ? b.name : '— None —')}
              placeholder="Optional"
              disabled={savingBranch}
            />
          ) : (
            <input
              id="qa-branch"
              type="text"
              value={settingsQaBranch}
              onChange={(e) => onSettingsQaBranchChange(e.target.value)}
              placeholder="Optional"
              className="input-mdl"
            />
          )}
        </div>
        <div>
          <label htmlFor="uat-branch" className="block text-sm font-medium text-slate-300 mb-1">
            UAT branch (optional)
          </label>
          {branches.length > 0 ? (
            <DropdownSearch
              id="uat-branch"
              options={[{ name: '' }, ...branches]}
              value={
                settingsUatBranch
                  ? (branches.find((b) => b.name === settingsUatBranch) ?? {
                      name: settingsUatBranch,
                    })
                  : { name: '' }
              }
              onChange={(b) => onSettingsUatBranchChange(b?.name ?? '')}
              getOptionValue={(b) => b.name}
              getOptionLabel={(b) => (b.name ? b.name : '— None —')}
              placeholder="Optional"
              disabled={savingBranch}
            />
          ) : (
            <input
              id="uat-branch"
              type="text"
              value={settingsUatBranch}
              onChange={(e) => onSettingsUatBranchChange(e.target.value)}
              placeholder="Optional"
              className="input-mdl"
            />
          )}
        </div>

        <div className="border-t border-dark-border pt-4 mt-2">
          <h3 className="text-sm font-semibold text-slate-100 mb-1">PR enrichment</h3>
          <p className="text-xs text-slate-400 mb-3">
            Cycle time uses merged PRs into your QA branch and merge cadence on UAT/deploy (branches
            above). Optional logins below are treated as bots when syncing PR reviews and comments.
          </p>
          <div className="space-y-3">
            <div>
              <label
                htmlFor="cycle-bot-logins"
                className="block text-sm font-medium text-slate-300 mb-1"
              >
                Extra bot logins to ignore (optional)
              </label>
              <textarea
                id="cycle-bot-logins"
                rows={3}
                className="w-full rounded-md border border-dark-border bg-dark-surface px-3 py-2 text-sm text-slate-100"
                placeholder="One GitHub login per line"
                value={cycleBotLoginsText}
                onChange={(e) => {
                  onCycleBotLoginsInteraction();
                  onCycleBotLoginsTextChange(e.target.value);
                }}
                disabled={savingBranch}
              />
              <p className="text-xs text-slate-400 mt-1">
                Standard bots are already excluded. These logins are ignored when computing first
                human review/comment times on PRs.
              </p>
            </div>
            <button
              type="button"
              className="text-sm font-medium text-vivid-red hover:underline"
              disabled={savingBranch}
              onClick={onResetCycleBotDefaults}
            >
              Reset to app defaults (clear saved config on Save)
            </button>
          </div>
        </div>
      </div>
    </ScrollableModal>
  );
}
